import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../services/apiService';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  BookOpen,
  Plus,
  Tag,
  Trash2,
  Lock,
  Unlock,
  ArrowRight,
  Loader2,
  AlertCircle,
  X
} from 'lucide-react';

interface Glossary {
  id: string;
  siteId: string;
  name: string;
  description?: string;
  domain?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    terms: number;
  };
}

const Glossaries: React.FC = () => {
  const navigate = useNavigate();
  const { siteId } = useParams<{ siteId?: string }>();

  const [glossaries, setGlossaries] = useState<Glossary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newGlossary, setNewGlossary] = useState({
    name: '',
    description: '',
    domain: ''
  });
  const [sites, setSites] = useState<any[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>(siteId || '');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadSites();
  }, []);

  useEffect(() => {
    if (selectedSiteId) {
      loadGlossaries();
    }
  }, [selectedSiteId]);

  const loadSites = async () => {
    try {
      const { sites } = await apiService.getSites(1, 100);
      setSites(sites);
      if (!selectedSiteId && sites.length > 0) {
        setSelectedSiteId(sites[0].id);
      }
    } catch (err: any) {
      console.error('Error loading sites:', err);
    }
  };

  const loadGlossaries = async () => {
    if (!selectedSiteId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getGlossaries(selectedSiteId);
      setGlossaries(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load glossaries');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGlossary = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSiteId) return;

    try {
      setCreating(true);
      await apiService.createGlossary({
        siteId: selectedSiteId,
        ...newGlossary
      });
      setShowCreateModal(false);
      setNewGlossary({ name: '', description: '', domain: '' });
      loadGlossaries();
    } catch (err: any) {
      alert(err.message || 'Failed to create glossary');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteGlossary = async (glossaryId: string) => {
    if (!window.confirm('Are you sure you want to delete this glossary? All terms will be deleted.')) {
      return;
    }

    try {
      await apiService.deleteGlossary(glossaryId);
      loadGlossaries();
    } catch (err: any) {
      alert(err.message || 'Failed to delete glossary');
    }
  };

  const handleToggleActive = async (glossary: Glossary) => {
    try {
      await apiService.updateGlossary(glossary.id, {
        isActive: !glossary.isActive
      });
      loadGlossaries();
    } catch (err: any) {
      alert(err.message || 'Failed to update glossary');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Glossaries</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Manage company-specific terminology and brand names
        </p>
      </div>

      {/* Site Selector */}
      <div className="mb-6">
        <Label className="mb-2 block">Select Site</Label>
        <select
          value={selectedSiteId}
          onChange={(e) => setSelectedSiteId(e.target.value)}
          className="flex h-10 w-full md:w-96 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <option value="">Select a site...</option>
          {sites.map(site => (
            <option key={site.id} value={site.id}>
              {site.name} ({site.domain})
            </option>
          ))}
        </select>
      </div>

      {selectedSiteId && (
        <>
          {/* Actions */}
          <div className="mb-6 flex justify-between items-center">
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Glossary
            </Button>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <CardHeader>
                    <Skeleton className="h-8 w-8 rounded-lg" />
                    <Skeleton className="h-6 w-32 mt-2" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-4 w-full mb-2" />
                    <Skeleton className="h-4 w-24" />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Error State */}
          {error && (
            <Card className="border-destructive/50 bg-destructive/10">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-destructive" />
                  <div>
                    <h3 className="font-medium text-destructive">Error</h3>
                    <p className="text-sm text-destructive/80">{error}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Glossaries Grid */}
          {!loading && !error && (
            <>
              {glossaries.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">No glossaries</h3>
                    <p className="text-muted-foreground mb-6">Get started by creating a new glossary.</p>
                    <Button onClick={() => setShowCreateModal(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      New Glossary
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {glossaries.map((glossary) => (
                    <Card
                      key={glossary.id}
                      className="hover:shadow-md transition-shadow"
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <BookOpen className="h-5 w-5 text-primary" />
                          </div>
                          <Badge variant={glossary.isActive ? 'default' : 'secondary'}>
                            {glossary.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        <CardTitle className="mt-3 truncate">{glossary.name}</CardTitle>
                        {glossary.description && (
                          <CardDescription className="line-clamp-2">
                            {glossary.description}
                          </CardDescription>
                        )}
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center text-sm text-muted-foreground mb-4">
                          <Tag className="h-4 w-4 mr-1" />
                          <span>{glossary._count?.terms || 0} terms</span>
                          {glossary.domain && (
                            <>
                              <span className="mx-2">•</span>
                              <span className="capitalize">{glossary.domain}</span>
                            </>
                          )}
                        </div>

                        <div className="flex items-center justify-between pt-3 border-t">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/glossaries/${glossary.id}`)}
                          >
                            Manage Terms
                            <ArrowRight className="h-4 w-4 ml-1" />
                          </Button>
                          <div className="flex space-x-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleToggleActive(glossary)}
                              title={glossary.isActive ? 'Deactivate' : 'Activate'}
                            >
                              {glossary.isActive ? (
                                <Lock className="h-4 w-4" />
                              ) : (
                                <Unlock className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteGlossary(glossary.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Create Glossary Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Create New Glossary</CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowCreateModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateGlossary} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    required
                    value={newGlossary.name}
                    onChange={(e) => setNewGlossary({ ...newGlossary, name: e.target.value })}
                    placeholder="E-commerce Terms"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newGlossary.description}
                    onChange={(e) => setNewGlossary({ ...newGlossary, description: e.target.value })}
                    rows={3}
                    placeholder="Product and shopping terminology"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="domain">Domain</Label>
                  <Input
                    id="domain"
                    value={newGlossary.domain}
                    onChange={(e) => setNewGlossary({ ...newGlossary, domain: e.target.value })}
                    placeholder="ecommerce, saas, medical, etc."
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={creating}>
                    {creating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Glossary'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Glossaries;
